import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { last, get } from 'lodash';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';

import { useBoolean } from 'src/hooks/use-boolean';

/* import {
  createTask,
  updateTask,
  deleteTask,
  clearColumn,
  updateColumn,
  deleteColumn,
} from 'src/api/kanban'; */

import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';

import KanbanTaskAdd from './kanban-task-add';
import KanbanTaskItem from './kanban-task-item';
import KanbanColumnToolBar from './kanban-column-tool-bar';

// ----------------------------------------------------------------------

export default function KanbanColumn({ column, tasks, index, board, setBoard, handleEmit, ticketType, fetchTask }) {
  const { enqueueSnackbar } = useSnackbar();
  const openAddTask = useBoolean();

  const handleUpdateSupportColumn = useCallback(
    async (data) => {
      try {
        const { columnName, columnType, nextColumnId, templateId, time } = data;
        if (!columnName.trim()) return;
        // if (column.name !== columnName) {
        // updateColumn(column.id, columnName);
        const slaRule = { nextColumnId, time: parseInt(time), templateId: parseInt(templateId) };
        const data = { id: column.id, newName: columnName, columnType, slaRule };
        handleEmit(data, 'updateColumn', (err, data) => {
          if (!err) {
            enqueueSnackbar('Column update Successfully!', {
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
            });
            setBoard((prevBoard) => {
              const newBoard = { ...prevBoard };
              const newColumn = newBoard.columns[column.id];
              newColumn.name = columnName;
              newColumn.slaRule = slaRule;
              newColumn.columnType = columnType;
              newBoard.columns[column.id] = newColumn;
              return newBoard;
            });
          } else {
            enqueueSnackbar(data, {
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
              variant: 'error',
            });
          }
        });
        // }
      } catch (error) {
        console.error(error);
      }
    },
    [board, column?.id, column?.name, enqueueSnackbar]
  );

  const handleUpdateColumn = useCallback(
    async (columnName) => {
      try {
        if (!columnName.trim()) return;
        if (column.name !== columnName) {
          // updateColumn(column.id, columnName);
          const data = { id: column.id, newName: columnName };
          handleEmit(data, 'updateColumn', (err, data) => {
            if (!err) {
              enqueueSnackbar('Column update Successfully!', {
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
              });
              setBoard((prevBoard) => {
                const newBoard = { ...prevBoard };
                const newColumn = newBoard.columns[column.id];
                newColumn.name = columnName;
                newBoard.columns[column.id] = newColumn;
                return newBoard;
              });
            } else {
              enqueueSnackbar(data, {
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
                variant: 'error',
              });
            }
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [board, column?.id, column?.name, enqueueSnackbar]
  );

  const handleClearColumn = useCallback(async () => {
    try {
      // clearColumn(column.id);
    } catch (error) {
      console.error(error);
    }
  }, [column?.id]);

  const handleDeleteColumn = useCallback(async () => {
    try {
      let error;
      setBoard((prevBoard) => {
        if (prevBoard.columns[column.id].taskIds.length > 0) {
          error = true;
        }
        return prevBoard;
      });
      if (error) {
        return enqueueSnackbar('This Column can not be deleted.', {
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          variant: 'error',
        });
      }

      handleEmit({ id: column.id }, 'deleteColumn', (err, data) => {
        if (!err) {
          setBoard((prevBoard) => {
            const newBoard = { ...prevBoard };
            delete newBoard.columns[column.id];

            const index = newBoard.ordered.indexOf(column.id);
            newBoard.ordered.splice(index, 1);

            return newBoard;
          });
          enqueueSnackbar('Delete success!', {
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          });
        } else {
          enqueueSnackbar(data, {
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            variant: 'error',
          });
          return;
        }
      });
    } catch (error) {
      console.error(error);
    }
  }, [column?.id, enqueueSnackbar]);

  const handleAddTask = useCallback(
    async (taskData) => {
      try {
        // createTask(column.id, taskData);

        openAddTask.onFalse();
      } catch (error) {
        console.error(error);
      }
    },
    [column?.id, openAddTask]
  );

  const handleUpdateTask = useCallback(async (data) => {
    try {
      setBoard((prevBoard) => {
        const newBoard = { ...prevBoard };
        newBoard.tasks[data.id] = data;
        return newBoard;
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleDeleteTask = useCallback(
    async (taskId) => {
      try {
        // deleteTask(column.id, taskId);

        handleEmit({ taskId, columnId: column?.id }, 'deleteTask', (err, data) => {
          if (!err) {
            enqueueSnackbar('Task Deleted successfully.', {
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
            });

            setBoard((prevBoard) => {
              const newBoard = { ...prevBoard };
              const columnData = { ...newBoard.columns[column.id] };
              columnData.tasks = columnData.tasks.filter((task) => task.id !== taskId);
              if (columnData.taskIds) {
                columnData.taskIds = columnData.taskIds.filter((id) => id !== taskId);
              }

              // UPDATE size
              columnData.size = (columnData.size || 1) - 1;

              newBoard.columns[column.id] = columnData;

              return newBoard;
            });
          } else {
            enqueueSnackbar('Insufficient permissions to delete.', {
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
              variant: 'error',
            });
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    [column?.id, enqueueSnackbar]
  );

  const renderAddTask = (
    <Stack
      spacing={2}
      sx={{
        pb: 3,
      }}
    >
      {openAddTask.value && (
        <KanbanTaskAdd status={column?.name} onAddTask={handleAddTask} onCloseAddTask={openAddTask.onFalse} />
      )}

      <Button
        fullWidth
        size="large"
        color="inherit"
        startIcon={
          <Iconify
            icon={openAddTask.value ? 'solar:close-circle-broken' : 'mingcute:add-line'}
            width={18}
            sx={{ mr: -0.5 }}
          />
        }
        onClick={openAddTask.onToggle}
        sx={{ fontSize: 14 }}
      >
        {openAddTask.value ? 'Close' : 'Add Task'}
      </Button>
    </Stack>
  );

  const numberOfItem = board?.filterTickets?.[column?.id]?.taskIds || [];

  const taskArray = Object.values(column.tasks || {});
  const lastSort = get(last(taskArray), 'sort') || [];
  return (
    <Draggable draggableId={column?.id} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{
            px: 1.6,
            borderRadius: '6px',
            bgcolor: 'background.neutral',
            ...(snapshot.isDragging && {
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.24),
            }),
          }}
        >
          <Stack {...provided.dragHandleProps}>
            <KanbanColumnToolBar
              column={column}
              columnName={column?.name}
              onUpdateColumn={handleUpdateColumn}
              onClearColumn={handleClearColumn}
              onDeleteColumn={handleDeleteColumn}
              columnLength={column?.size || 0}
              ticketType={ticketType}
              handleUpdateSupportColumn={handleUpdateSupportColumn}
            />

            <Droppable droppableId={column?.id} type="TASK">
              {(dropProvided) => (
                <Stack
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  spacing={1}
                  sx={{
                    pt: 1,
                    pb: 3,
                    width: 280,
                  }}
                >
                  {column && column?.tasks && column.tasks.length > 0
                    ? column.tasks.map((task, taskIndex) => (
                        <KanbanTaskItem
                          key={task.id || task.docId || taskIndex}
                          index={taskIndex}
                          task={task}
                          onUpdateTask={handleUpdateTask}
                          onDeleteTask={() => {
                            console.log('Deleting task - ID:', task.id, 'docId:', task.docId);
                            return handleDeleteTask(task.id || task.docId);
                          }}
                          board={board}
                          setBoard={setBoard}
                          handleEmit={handleEmit}
                          ticketType={ticketType}
                          columnId={column?.id}
                        />
                      ))
                    : null}

                  {dropProvided.placeholder}
                  <Button
                    onClick={() => fetchTask(column?.id, board?.filterTickets, lastSort)}
                    sx={{
                      display: Object.keys(column?.tasks || {}).length < (column.size || 0) ? 'block' : 'none',
                    }}
                  >
                    Load More
                  </Button>
                </Stack>
              )}
            </Droppable>

            {/* {renderAddTask} */}
          </Stack>
        </Paper>
      )}
    </Draggable>
  );
}

KanbanColumn.propTypes = {
  column: PropTypes.object,
  index: PropTypes.number,
  tasks: PropTypes.object,
};
